'use client'

import { AtSign, Hash, Settings, User, Wrench } from 'lucide-react'
import type * as React from 'react'

import { cn } from '@/lib/utils'

import type { MentionItem } from '../../markdown-editor.interface'

const categoryIcons: Record<string, React.ReactNode> = {
  user: <User className="size-4" />,
  variable: <Hash className="size-4" />,
  tool: <Wrench className="size-4" />,
  setting: <Settings className="size-4" />,
  default: <AtSign className="size-4" />,
}

const getIcon = (item: MentionItem): React.ReactNode => {
  if (item.icon) {
    return categoryIcons[item.icon] ?? categoryIcons.default
  }
  return categoryIcons[item.category ?? 'default'] ?? categoryIcons.default
}

export interface MentionItemButtonProps {
  item: MentionItem
  isSelected: boolean
  onSelect: () => void
  onHover: () => void
}

const buttonClasses = cn(
  'flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm outline-none',
  'hover:bg-accent hover:text-accent-foreground',
  'focus:bg-accent focus:text-accent-foreground'
)

/**
 * Individual mention item button in the suggestion list
 */
export const MentionItemButton = ({
  item,
  isSelected,
  onSelect,
  onHover,
}: MentionItemButtonProps) => (
  <button
    type="button"
    onClick={onSelect}
    onMouseEnter={onHover}
    className={cn(buttonClasses, isSelected && 'bg-accent text-accent-foreground')}
  >
    <span className="flex size-6 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
      {getIcon(item)}
    </span>
    <div className="flex flex-1 flex-col overflow-hidden">
      <span className="truncate font-medium">{item.label}</span>
      {item.description && (
        <span className="truncate text-muted-foreground text-xs">{item.description}</span>
      )}
    </div>
    {item.category && (
      <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-muted-foreground text-xs">
        {item.category}
      </span>
    )}
  </button>
)

MentionItemButton.displayName = 'MentionItemButton'
